import { Inject, Injectable } from '@angular/core';
import { TranslationService } from 'gast-common-ng';

/**
 * Class contains URL Paths for http requests
 */
@Injectable()
export class UrlService {

    environment;

    constructor(@Inject('env') private envConfig,
        private translationService: TranslationService) {
        this.environment = this.envConfig.callback(this.envConfig.value);
    }

    // COURSES
    public getCourses(): string {
        return `${this.environment.courseServiceUrl}api/public/courses`;
    }

    public getCourseFormats(): string {
        return `${this.environment.courseServiceUrl}api/public/courses/format`;
    }

    public getCourseClassificationCheck(): string {
        return `${this.environment.courseServiceUrl}api/my-account/classification`;
    }

    public getBookingConfirmation(): string {
        return `${this.environment.courseServiceUrl}api/my-account/confirmation`;
    }

    public saveBookingIdPair(): string {
        return `${this.environment.courseServiceUrl}api/my-account/booking-id-pair`;
    }

    // Languages for DUO
    public getAllLanguages(): string {
        return `${this.environment.coreServiceUrl}api/public/languages/duo`;
    }

    // get code encash validations
    public getCodeEncashRequiredPersonalIdent(): string {
        return `${this.environment.courseServiceUrl}api/public/code-encash/required-personal-ident`;
    }

    // submit code encash validations
    public getCodeEncashValidatePersonalIdent(): string {
        return `${this.environment.courseServiceUrl}api/public/code-encash/validate-personal-ident`;
    }

    // get imported person identification
    public getImportedPersonalIdent(): string {
        return `${this.environment.courseServiceUrl}api/public/code-encash/personal-identifications`;
    }

    // get duo program
    public getDuoProgram(): string {
        return `${this.environment.courseServiceUrl}api/public/code-encash/duo-program`;
    }

    // get core person identification
    public getCorePersonalIdent(): string {
        return `${this.environment.courseServiceUrl}api/webshop/personal-identifications`;
    }

    // get self assessment result
    public getSelfAssessmentResult(): string {
        return `${this.environment.courseServiceUrl}api/public/self-assessments`;
    }

    public getCreateBookingAndCourseArticle(): string {
        return `${this.environment.courseServiceUrl}api/courses/bookings`;
    }

    // get code encash with courses for the given code
    public getCodeEncashWithCourses(): string {
        return `${this.environment.courseServiceUrl}api/public/courses/code-encash-with-courses`;
    }

    public getLearnerPortalUrl(): string {
        return `${this.environment.duoLearnerPortalUrl}`;
    }

    public getHeidelpayServiceUrl(): string {
        return `${this.environment.hcoServiceUrl}`;
    }

    public getHcoErroCodeUrl(): string {
        return `${this.environment.bookingServiceUrl}api/hco-error-code/`;
    }

    public getWebshopStepUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/steps`;
    }

    public getWebshopConfigUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/webshop-config`;
    }

    public getWebshopViewStepUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/views`;
    }

    public getWebshopDatafileUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/datafile/webshop-config`;
    }

    public getDuoUniversitiesUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/duo-universities`;
    }

    public getCountriesUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/countries`;
    }

    public getIntakeRegistrationURL(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/intake-registration`;
    }

    public getValidateLoggedUserForIntakeUrl(): string {
        return `${this.environment.courseServiceUrl}api/webshop/validate-logged-person-code`;
    }

    public getDataPrivacyProtectionUrl(): string {
        return `https://www.deutsch-uni.com/gast/duo/info/index/datenschutz.jsp`;
    }

    public getTermOfServiceUrl(): string {
        const lang = this.translationService.getCurrentLanguageCode().toLocaleLowerCase();
        switch (lang) {
            case 'de':
                return 'http://www.deutsch-uni.com/de/agb';
            case 'en':
                return 'http://www.deutsch-uni.com/en/gtc';
            default:
                return 'http://www.deutsch-uni.com/de/agb';
        }
    }

    public getClassificationDeadlineDateUrl(): string {
        return `${this.environment.courseServiceUrl}api/my-account/classification-deadline`;
    }

    public getIsfAlemaoUrl(): string {
        return `https://www.isf-alemao.de/`;
    }

    public getPublicCourseScheduleUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/course-schedules`;
    }

    public getPublicCategoryUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/categories`;
    }

    public getPublicLanguageGradingResultUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/language-grading-results`;
    }

    public getPublicLearnFormatUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/learn-formats`;
    }

    public getIndividualRegistrationURL(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop/individual-registration`;
    }

    public getPersonalDataUrl() {
        return `${this.environment.duoLearnerPortalUrl}dashboard/my-data`;
    }

    public getBookNextCourseUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/intakes/next-course`;
    }

    public getBookingWithVoucherUrl(): string {
        return `${this.environment.courseServiceUrl}api/courses/bookings-with-voucher`;
    }

    getTeaserUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/webshop-teaser`;
    }

    // portal account
    public getPortalAccountLoginUrl(): string {
        return `${this.environment.accountPortalUrl}login`;
    }

    public getPortalAccountRegisterUrl(): string {
        return `${this.environment.accountPortalUrl}register`;
    }

    public getLearnWithDuoUrl(): string {
        return `http://www.deutsch-uni.com/lernen-mit-duo`;
    }

    public getInstitutionUrl(): string {
        return `http://www.deutsch-uni.com/institutionen`;
    }

    public getAboutDuoUrl(): string {
        return `http://www.deutsch-uni.com/ueber-duo`;
    }

    public getWebshopPortalUrl(): string {
        return `${this.environment.duoWebshopUrl}course`;
    }

    public getRegistrationInfoEventUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/registration-info-event`;
    }

    public getRegistrationInfoEventConfirmMailUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/registration-info-event/confirm-mail`;
    }

    public getRegistrationLectorTrainingUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/registration-lector-training`;
    }

    public getOnsiteLectorRegisterUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/onsite-lector/register`;
    }

    public getOnsiteLectorLoginUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/onsite-lector/login`;
    }

    public getOnsiteLectorSelectCourseUrl(): string {
        return `${this.environment.courseServiceUrl}api/public/onsite-lector/select-course`;
    }

  public getAlreadyBooked(): string {
    return `${this.environment.bookingServiceUrl}api/booking/course-schedule-id`;
  }

  public checkAndSavePKZ(): string {
    return `${this.environment.courseServiceUrl}api/programs/register-daad`;
  }

  public checkAndSaveUniversity(): string {
    return `${this.environment.courseServiceUrl}api/programs/register-dfh`;
  }

  public mePersonUrl(): string {
    return `${this.environment.coreServiceUrl}api/users/me/persons`;
  }
}
